<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
        Active Store Users
      </h3>
    </div>

    <!-- Empty State -->
    <div v-if="users.length === 0" class="px-6 py-12 text-center">
      <svg
        class="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M17 20h5v-2a3 3 0 00-5.856-1.487M15 10a3 3 0 11-6 0 3 3 0 016 0zM6 20a9 9 0 0118 0v2h2v-2a11 11 0 10-20 0v2h2v-2z"
        />
      </svg>
      <p class="mt-2 text-gray-600 dark:text-gray-400">No active users yet</p>
    </div>

    <!-- Loading State -->
    <div v-else-if="isLoading" class="px-6 py-12 text-center">
      <div class="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400">
        <svg class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Loading...</span>
      </div>
    </div>

    <!-- Users List -->
    <div v-else class="divide-y divide-gray-200 dark:divide-gray-700">
      <div
        v-for="user in users"
        :key="user.id"
        class="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 min-w-0">
            <!-- User Name -->
            <p class="text-sm font-medium text-gray-900 dark:text-white">
              {{ user.names || user.username }}
            </p>

            <!-- Contact Info -->
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
              <span v-if="user.email" class="inline-flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {{ user.email }}
              </span>
              <span v-else class="inline-flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {{ user.phone_number || 'N/A' }}
              </span>
            </p>

            <!-- Permissions with Edit -->
            <div class="mt-3">
              <div v-if="editingUserId === user.id" class="space-y-2">
                <div class="space-y-2 max-h-48 overflow-y-auto">
                  <div v-for="group in permissionGroups" :key="group.key">
                    <p class="px-2 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {{ group.label }}
                    </p>
                    <label
                      v-for="permission in group.options"
                      :key="permission.value"
                      class="flex items-start gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded"
                    >
                      <input
                        type="checkbox"
                        :checked="editingPermissions.includes(permission.value)"
                        @change="updateEditingPermissions(permission.value, $event.target.checked)"
                        class="w-4 h-4 mt-0.5 text-blue-500 rounded border-gray-300 dark:border-gray-600"
                      />
                      <span>
                        <strong class="block text-sm text-gray-700 dark:text-gray-300">
                          {{ permission.label }}
                        </strong>
                        <small class="block text-xs text-gray-500 dark:text-gray-400">
                          {{ permission.description }}
                        </small>
                      </span>
                    </label>
                  </div>
                </div>
                <div class="flex gap-2">
                  <button
                    @click="savePermissions(user.user_store_permission_id)"
                    class="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Save
                  </button>
                  <button
                    @click="cancelEdit"
                    class="px-3 py-1 text-sm bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-white rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <div v-else class="flex flex-wrap gap-1">
                <span
                  v-for="permission in user.permissions"
                  :key="permission"
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                >
                  {{ formatPermission(permission) }}
                </span>
              </div>
            </div>

            <!-- Joined Info -->
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Joined: {{ formatDate(user.created_at) }}
            </p>
          </div>

          <!-- Actions -->
          <div class="flex gap-2">
            <button
              v-if="editingUserId !== user.id"
              @click="startEditPermissions(user)"
              title="Edit permissions"
              class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              @click="removeUser(user.user_store_permission_id)"
              title="Remove user"
              class="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import {
  closePermissionDependencies,
  getPermissionSummaryLabel,
  groupPermissionCatalog,
  updatePermissionSelection,
} from '@/helper/storePermissions';

export default {
  name: 'StoreUsersList',
  props: {
    storeId: {
      type: [String, Number],
      required: true,
    },
    users: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update-permissions', 'remove-user'],
  setup(props, { emit }) {
    const editingUserId = ref(null);
    const editingPermissions = ref([]);

    const availablePermissions = [
      'CAN_MANAGE_USERS',
      'CAN_MANAGE_INVENTORY',
      'CAN_MANAGE_REPORTS',
      'CAN_VIEW_REPORTS',
    ];

    const permissionGroups = computed(() =>
      groupPermissionCatalog(availablePermissions),
    );

    const formatPermission = (permission) =>
      getPermissionSummaryLabel(permission);

    const formatDate = (date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    const startEditPermissions = (user) => {
      editingUserId.value = user.id;
      editingPermissions.value = closePermissionDependencies(user.permissions);
    };

    const updateEditingPermissions = (permission, selected) => {
      editingPermissions.value = updatePermissionSelection(
        editingPermissions.value,
        permission,
        selected,
        availablePermissions,
      );
    };

    const savePermissions = (permissionId) => {
      emit(
        'update-permissions',
        permissionId,
        closePermissionDependencies(editingPermissions.value),
      );
      editingUserId.value = null;
    };

    const cancelEdit = () => {
      editingUserId.value = null;
      editingPermissions.value = [];
    };

    const removeUser = (permissionId) => {
      if (!confirm('Are you sure you want to remove this user from the store?')) {
        return;
      }
      emit('remove-user', permissionId);
    };

    return {
      editingUserId,
      editingPermissions,
      availablePermissions,
      permissionGroups,
      formatPermission,
      formatDate,
      startEditPermissions,
      updateEditingPermissions,
      savePermissions,
      cancelEdit,
      removeUser,
    };
  },
};
</script>
